import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Select,
  Spinner,
  Text,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';

import {
  LossControlBranch,
  LossControlClassification,
  LossControlRequestedScope,
  lossControlService,
} from '../../../../services/lossControlService';

interface LossControlResponsibilitiesFieldProps {
  value: LossControlRequestedScope[];
  onChange: (scopes: LossControlRequestedScope[]) => void;
  isDisabled?: boolean;
}

function scopeKey(scope: LossControlRequestedScope): string {
  return [
    scope.branchCode,
    scope.familyCode,
    scope.groupCode || '',
    scope.subgroupCode || '',
  ].join('|');
}

const LossControlResponsibilitiesField: React.FC<
  LossControlResponsibilitiesFieldProps
> = ({ value, onChange, isDisabled = false }) => {
  const [branches, setBranches] = useState<LossControlBranch[]>([]);
  const [families, setFamilies] = useState<LossControlClassification[]>([]);
  const [groups, setGroups] = useState<LossControlClassification[]>([]);
  const [subgroups, setSubgroups] = useState<LossControlClassification[]>([]);
  const [branchCode, setBranchCode] = useState('');
  const [familyCode, setFamilyCode] = useState('');
  const [groupCode, setGroupCode] = useState('');
  const [subgroupCode, setSubgroupCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);

    lossControlService
      .listBranches()
      .then(response => {
        if (active) setBranches(response);
      })
      .catch(() => {
        if (active) {
          setError(
            'Não foi possível consultar as filiais do Controle de Perdas.',
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setFamilyCode('');
    setGroupCode('');
    setSubgroupCode('');
    setFamilies([]);
    setGroups([]);
    setSubgroups([]);

    if (!branchCode) return undefined;
    setLoading(true);
    setError('');
    lossControlService
      .listFamilies(branchCode)
      .then(response => {
        if (active) setFamilies(response);
      })
      .catch(() => {
        if (active)
          setError('Não foi possível consultar as famílias da filial.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [branchCode]);

  useEffect(() => {
    let active = true;
    setGroupCode('');
    setSubgroupCode('');
    setGroups([]);
    setSubgroups([]);

    if (!branchCode || !familyCode) return undefined;
    setLoading(true);
    setError('');
    lossControlService
      .listGroups(branchCode, familyCode)
      .then(response => {
        if (active) setGroups(response);
      })
      .catch(() => {
        if (active)
          setError('Não foi possível consultar os grupos da família.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [branchCode, familyCode]);

  useEffect(() => {
    let active = true;
    setSubgroupCode('');
    setSubgroups([]);

    if (!branchCode || !familyCode || !groupCode) return undefined;
    setLoading(true);
    setError('');
    lossControlService
      .listSubgroups(branchCode, familyCode, groupCode)
      .then(response => {
        if (active) setSubgroups(response);
      })
      .catch(() => {
        if (active) setError('Não foi possível consultar os subgrupos.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [branchCode, familyCode, groupCode]);

  const selectedBranch = useMemo(
    () => branches.find(item => item.id === branchCode),
    [branchCode, branches],
  );
  const selectedFamily = useMemo(
    () => families.find(item => item.code === familyCode),
    [families, familyCode],
  );
  const selectedGroup = useMemo(
    () => groups.find(item => item.code === groupCode),
    [groupCode, groups],
  );
  const selectedSubgroup = useMemo(
    () => subgroups.find(item => item.code === subgroupCode),
    [subgroupCode, subgroups],
  );

  const addScope = () => {
    if (!selectedBranch || !selectedFamily) return;

    const scope: LossControlRequestedScope = {
      branchCode: selectedBranch.id,
      branchName: selectedBranch.name,
      familyCode: selectedFamily.code,
      familyName: selectedFamily.name,
      groupCode: selectedGroup?.code || null,
      groupName: selectedGroup?.name || null,
      subgroupCode: selectedSubgroup?.code || null,
      subgroupName: selectedSubgroup?.name || null,
    };

    if (!value.some(item => scopeKey(item) === scopeKey(scope))) {
      onChange([...value, scope]);
    }
  };

  const removeScope = (key: string) => {
    onChange(value.filter(scope => scopeKey(scope) !== key));
  };

  return (
    <Box mt="5" pt="4" borderTopWidth="1px">
      <Flex align="center" justify="space-between" mb="2">
        <Box>
          <Text fontWeight="bold">Responsabilidades no Controle de Perdas</Text>
          <Text fontSize="sm" color="gray.500">
            Um operador pode responder por várias classificações.
          </Text>
        </Box>
        {loading && <Spinner size="sm" color="green.400" />}
      </Flex>

      {error && (
        <Alert status="error" borderRadius="md" mb="3" fontSize="sm">
          <AlertIcon />
          {error}
        </Alert>
      )}

      <Flex direction="column" gridGap="3">
        <FormControl>
          <FormLabel mb="1">Filial</FormLabel>
          <Select
            value={branchCode}
            onChange={event => setBranchCode(event.target.value)}
            isDisabled={isDisabled || loading}
            placeholder="Selecione a filial"
          >
            {branches.map(branch => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel mb="1">Família</FormLabel>
          <Select
            value={familyCode}
            onChange={event => setFamilyCode(event.target.value)}
            isDisabled={isDisabled || loading || !branchCode}
            placeholder="Selecione a família"
          >
            {families.map(family => (
              <option key={family.code} value={family.code}>
                {family.name}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel mb="1">Grupo (opcional)</FormLabel>
          <Select
            value={groupCode}
            onChange={event => setGroupCode(event.target.value)}
            isDisabled={isDisabled || loading || !familyCode}
          >
            <option value="">Toda a família</option>
            {groups.map(group => (
              <option key={group.code} value={group.code}>
                {group.name}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel mb="1">Subgrupo (opcional)</FormLabel>
          <Select
            value={subgroupCode}
            onChange={event => setSubgroupCode(event.target.value)}
            isDisabled={isDisabled || loading || !groupCode}
          >
            <option value="">Todo o grupo</option>
            {subgroups.map(subgroup => (
              <option key={subgroup.code} value={subgroup.code}>
                {subgroup.name}
              </option>
            ))}
          </Select>
        </FormControl>

        <Button
          leftIcon={<FiPlus />}
          colorScheme="green"
          variant="outline"
          onClick={addScope}
          isDisabled={isDisabled || loading || !familyCode}
          type="button"
        >
          Adicionar responsabilidade
        </Button>
      </Flex>

      <Flex direction="column" gridGap="2" mt="4">
        {value.length === 0 ? (
          <Text fontSize="sm" color="gray.500">
            Nenhuma responsabilidade adicionada. Os produtos ficarão em “Sem
            responsável”.
          </Text>
        ) : (
          value.map(scope => {
            const key = scopeKey(scope);
            return (
              <Flex
                key={key}
                align="center"
                justify="space-between"
                borderWidth="1px"
                borderRadius="md"
                p="3"
                gridGap="3"
              >
                <Box>
                  <Text fontWeight="semibold" fontSize="sm">
                    {scope.branchName}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {[scope.familyName, scope.groupName, scope.subgroupName]
                      .filter(Boolean)
                      .join(' / ')}
                  </Text>
                </Box>
                <IconButton
                  aria-label="Remover responsabilidade"
                  icon={<Icon as={FiTrash2} />}
                  colorScheme="red"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeScope(key)}
                  isDisabled={isDisabled}
                />
              </Flex>
            );
          })
        )}
      </Flex>
    </Box>
  );
};

export default LossControlResponsibilitiesField;
